import { supabase } from '@/integrations/supabase/client';

export interface StaffComment {
  id: string;
  comment_text: string;
  user_id: string;
  video_content_id: string;
  created_at: string;
  author_first_name?: string;
  author_last_name?: string;
  author_avatar_url?: string;
}

/**
 * Fetch staff-visibility comments for a video content
 */
export async function fetchStaffComments(videoContentId: string): Promise<{ success: boolean; data?: StaffComment[]; error?: string }> {
  try {
    const { data, error } = await supabase
      .from('video_comments')
      .select('id, comment_text, user_id, video_content_id, created_at')
      .eq('video_content_id', videoContentId)
      .eq('visibility', 'staff')
      .order('created_at', { ascending: true });

    if (error) return { success: false, error: error.message };

    // Fetch author info for each unique user_id
    const userIds = [...new Set((data || []).map(c => c.user_id))];
    let usersMap: Record<string, { first_name: string | null; last_name: string | null; avatar_url: string | null }> = {};

    if (userIds.length > 0) {
      const { data: users } = await supabase
        .from('users')
        .select('id, first_name, last_name, avatar_url')
        .in('id', userIds);
      if (users) {
        usersMap = Object.fromEntries(users.map(u => [u.id, u]));
      }
    }

    const comments: StaffComment[] = (data || []).map(c => ({
      ...c,
      author_first_name: usersMap[c.user_id]?.first_name || undefined,
      author_last_name: usersMap[c.user_id]?.last_name || undefined,
      author_avatar_url: usersMap[c.user_id]?.avatar_url || undefined,
    }));

    return { success: true, data: comments };
  } catch (err: any) {
    return { success: false, error: err.message };
  }
}

/**
 * Create a staff-visibility comment
 */
export async function createStaffComment(params: {
  videoContentId: string;
  userId: string;
  text: string;
}): Promise<{ success: boolean; data?: StaffComment; error?: string }> {
  try {
    const { data, error } = await supabase
      .from('video_comments')
      .insert({
        video_content_id: params.videoContentId,
        user_id: params.userId,
        comment_text: params.text,
        visibility: 'staff',
        timestamp_seconds: null,
        video_version: null,
      })
      .select('id, comment_text, user_id, video_content_id, created_at')
      .single();

    if (error) return { success: false, error: error.message };

    // Fetch author info
    const { data: user } = await supabase
      .from('users')
      .select('first_name, last_name, avatar_url')
      .eq('id', params.userId)
      .single();

    return {
      success: true,
      data: {
        ...data,
        author_first_name: user?.first_name || undefined,
        author_last_name: user?.last_name || undefined,
        author_avatar_url: user?.avatar_url || undefined,
      },
    };
  } catch (err: any) {
    return { success: false, error: err.message };
  }
}
